import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb2"
import { withTranslation } from "react-i18next"
import moment from "moment"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import BootstrapTable from "react-bootstrap-table-next"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  PaginationTotalStandalone,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import {
  getActionsList,
  getActionTypes,
  getListUsersAudit,
} from "services/audit/calls"

const AuditoriaList = props => {
  document.title = "Trilha Auditoria | Sensetek"
  moment.locale("pt-br")

  const limit = 30

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [totalRecords, setTotalRecords] = useState(0)

  const [actionsList, setActionsList] = useState([])

  const [inputValue, setInputValue] = useState("")

  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState({
    users: [],
    actions: [],
    startAt: moment().startOf("day").toDate(),
    endAt: moment().endOf("day").toDate(),
  })

  const [actionOptions, setActionOptions] = useState([])

  const paginationOptions = {
    custom: true,
    page: currentPage,
    sizePerPage: limit,
    totalSize: totalRecords,
    onPageChange: async (page, sizePerPage) => {
      setCurrentPage(page)
      setIsLoading(true)
      const q = {
        users: query?.users?.map(u => u.value),
        actionTypes: query?.actionTypes?.map(a => a.value),
        startAt: moment(query.startAt).startOf("day").toDate(),
        endAt: moment(query.endAt).endOf("day").toDate(),
      }
      try {
        const {
          actions,
          total,
          pagination: { totalPages },
        } = await getActionsList(q, page, limit)
        setActionsList(actions)
        setTotalRecords(total)
        setTotalPages(totalPages)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    showTotal: true,
    hideSizePerPage: false,
    prePageTitle: "Anterior",
    nextPageTitle: "Próxima",
    lastPageTitle: "Última Página",
    firstPageTitle: "Primeira Página",
    paginationTotalRenderer: (from, to, size) => (
      <span>
        Exibindo {from} a {to} de {size} registros. {totalPages} páginas.
      </span>
    ),
  }

  const defaultSorted = [
    {
      dataField: "shippingTrackerCode",
      order: "asc",
    },
  ]

  const selectRow = {
    mode: "checkbox",
    hideSelectColumn: true,
  }

  const columns = [
    {
      dataField: "id",
      text: "#",
      sort: false,
      headerStyle: {
        width: "100px",
      },
      formatter: (cellContent, row) => <b>{cellContent}</b>,
    },
    {
      dataField: "user.name",
      text: "Usuário",
      sort: false,
      formatter: (cellContent, row) => <b>{cellContent}</b>,
    },
    {
      dataField: "actionType.label",
      text: "Ação",
      sort: false,
      formatter: (cellContent, row) => <b>{cellContent}</b>,
    },
    {
      dataField: "createdAt",
      text: "Data/Hora",
      headerAlign: "center",
      sort: false,
      align: "center",
      formatter: (cellContent, row) => (
        <b>
          {moment(cellContent, "YYYY-MM-DD HH:mm:ss")
            .add(-3, "hours")
            .format("DD/MM/YYYY HH:mm:ss")}
        </b>
      ),
    },
  ]

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const actionTypes = await getActionTypes()
      setActionOptions(actionTypes)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchActions = async (query, page = 1, limit = 30) => {
    setIsLoading(true)
    try {
      const q = {
        users: query?.users?.map(u => u.value),
        actionTypes: query?.actionTypes?.map(a => a.value),
        startAt: moment(query.startAt).startOf("day").toDate(),
        endAt: moment(query.endAt).endOf("day").toDate(),
      }
      const {
        actions,
        total,
        pagination: { totalPages },
      } = await getActionsList(q, page, limit)
      setActionsList(actions)
      setTotalRecords(total)
      setTotalPages(totalPages)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onQuery = e => {
    e.preventDefault()
    fetchActions(query)
  }

  const loadOptions = async inputValue => {
    if (inputValue.length < 3) {
      return []
    }
    try {
      const response = await getListUsersAudit(inputValue)
      return response
    } catch (error) {
      console.error("Erro ao buscar usuários:", error.message)
      return []
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={props.t("Trilha Auditoria")}
            breadcrumbItems={[{ title: props.t("Lista de Ações"), path: "#" }]}
          />
          <Row>
            <Col>
              <Card className="mb-0 border-bottom">
                <CardHeader tag={"h5"} className="bg-transparent border-bottom">
                  Filtro
                </CardHeader>
                <CardBody>
                  <Form className="form-horizontal" onSubmit={onQuery}>
                    <Row>
                      <Col lg={4}>
                        <FormGroup>
                          <Label>Usuários</Label>
                          <AsyncSelect
                            isMulti
                            cacheOptions
                            defaultOptions={false}
                            loadOptions={loadOptions}
                            onInputChange={value => {
                              setInputValue(value)
                              return value
                            }}
                            onChange={e => {
                              setQuery({ ...query, users: e })
                            }}
                            value={query?.users}
                            placeholder="Buscar usuários..."
                            noOptionsMessage={() => {
                              return inputValue.length < 3
                                ? "Digite pelo menos 3 caracteres"
                                : "Nenhum resultado encontrado"
                            }}
                            menuPortalTarget={document.body}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={4}>
                        <FormGroup>
                          <Label>Ações</Label>
                          <Select
                            classNamePrefix="reactSelect"
                            options={actionOptions}
                            placeholder=""
                            isMulti
                            isDisabled={isLoading}
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => {
                              return "Sem Opções"
                            }}
                            value={query?.actionTypes}
                            onChange={e => {
                              setQuery({ ...query, actionTypes: e })
                            }}
                            menuPortalTarget={document.body}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={2}>
                        <FormGroup>
                          <Label>Início</Label>
                          <DatePicker
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            selected={query.startAt}
                            selectsStart
                            disabled={isLoading}
                            startDate={query.startAt}
                            endDate={query.endAt}
                            onChange={e => {
                              setQuery({
                                ...query,
                                startAt: e,
                              })
                            }}
                            popperProps={{ strategy: "fixed" }}
                            portalId="root-portal"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={2}>
                        <FormGroup>
                          <Label>Fim</Label>
                          <DatePicker
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            selected={query.endAt}
                            selectsEnd
                            disabled={isLoading}
                            startDate={query.startAt}
                            endDate={query.endAt}
                            onChange={e => {
                              setQuery({
                                ...query,
                                endAt: e,
                              })
                            }}
                            popperProps={{ strategy: "fixed" }}
                            portalId="root-portal"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <div
                          className="d-flex flex-row-reverse vstack gap-2"
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <button
                            className="btn btn-primary btn-block"
                            type="submit"
                          >
                            Filtrar
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card>
                <CardHeader tag={"h5"} className="bg-transparent border-bottom">
                  Resultado
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                    <div
                      className="accordion-body"
                      style={{ textAlign: "center" }}
                    >
                      <Spinner className="ms-2" color="primary" />
                    </div>
                  ) : actionsList?.length > 0 ? (
                    <PaginationProvider
                      pagination={paginationFactory(paginationOptions)}
                    >
                      {({ paginationTableProps, paginationProps }) => (
                        <ToolkitProvider
                          keyField="id"
                          columns={columns}
                          data={actionsList}
                        >
                          {toolkitProps => (
                            <React.Fragment>
                              <Row>
                                <Col>
                                  <PaginationListStandalone
                                    {...paginationProps}
                                  />
                                </Col>
                              </Row>
                              <Row>
                                <Col xl="12" className="table-responsive">
                                  <BootstrapTable
                                    keyField={"id"}
                                    bordered={true}
                                    striped={true}
                                    condensed
                                    defaultSorted={defaultSorted}
                                    selectRow={selectRow}
                                    classes={
                                      "table table-sm align-middle table-nowrap"
                                    }
                                    remote={{
                                      filter: false,
                                      pagination: true,
                                      sort: false,
                                      cellEdit: false,
                                    }}
                                    onTableChange={(type, newState) => {}}
                                    headerWrapperClasses={"thead-light"}
                                    {...toolkitProps.baseProps}
                                    {...paginationTableProps}
                                  />
                                  {/* </div> */}
                                </Col>
                              </Row>

                              <PaginationListStandalone {...paginationProps} />
                              <PaginationTotalStandalone {...paginationProps} />
                            </React.Fragment>
                          )}
                        </ToolkitProvider>
                      )}
                    </PaginationProvider>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <h5>Nenhum dado foi encontrado.</h5>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

AuditoriaList.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(AuditoriaList)
